import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { PromocodesService } from '../../../services/payments/promocodes/promocodes.service';
import { PromoCode, PromoType } from '../../../models/payment/promocode.model';
import { CreatePromocodeRequest, UpdatePromocodeRequest } from '../../../models/payment/promocodeRequests';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PaginatorModule } from 'primeng/paginator';

@Component({
  selector: 'app-promocodes',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, ButtonModule, ToastModule, TooltipModule,
    DialogModule, InputTextModule, InputTextareaModule, DropdownModule, InputNumberModule, AutoCompleteModule,
    ProgressSpinnerModule, PaginatorModule
  ],
  providers: [MessageService],
  templateUrl: './promocodes.component.html',
  styleUrl: './promocodes.component.scss'
})
export class PromocodesComponent implements OnInit {

  promoForm!: FormGroup;
  filterForm!: FormGroup;
  displayDialog = false;
  isEditMode = false;
  submitted = false;
  PromoType = PromoType;
  promocodes: PromoCode[] = [];
  names: any[] = [];
  isLoading: boolean = false;
  stateOptions = [
    { name: 'Active', value: 'Active' },
    { name: 'Inactive', value: 'Inactive' }
  ];

  appliedFilters = {
    code: '',
    promoStatus: ''
  };

  isSearching = false;

  types = [
    { label: 'Percentage', value: PromoType.Percentage },
    { label: 'Fixed Amount', value: PromoType.FixedAmount }
  ];

  first: number = 0;
  rows: number = 10;

  constructor(
    private messageService: MessageService,
    private promoService: PromocodesService,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    this.initializeForms();
    this.loadPromocodes();
    this.setupValueChangeListeners();
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  initializeForms() {


    this.filterForm = this.formBuilder.group({
      code: [null],
      promoStatus: [null]
    });

    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);

    this.promoForm = this.formBuilder.group({
      id: [0],
      code: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],
      type: [PromoType.Percentage, [Validators.required]],
      value: [null, [Validators.required, Validators.min(0.01)]],
      usageLimit: [null, [Validators.required, Validators.min(1)]],
      validityFrom: [today.toISOString().split('T')[0], [Validators.required]],
      validityTo: [nextMonth.toISOString().split('T')[0], [Validators.required]],
      status: ['Active']
    }, { validators: this.dateRangeValidator });
  }

  setupValueChangeListeners() {

    this.promoForm.get('type')?.valueChanges.subscribe(() => {
      this.promoForm.get('value')?.updateValueAndValidity();
    });

    this.promoForm.get('value')?.valueChanges.subscribe(() => {
      this.validatePercentage();
    });
  }

  dateRangeValidator(group: FormGroup) {
    const from = group.get('validityFrom')?.value;
    const to = group.get('validityTo')?.value;

    if (from && to && new Date(to) <= new Date(from)) {
      return { dateRange: true };
    }
    return null;
  }

  validatePercentage() {
    const typeControl = this.promoForm.get('type');
    const valueControl = this.promoForm.get('value');

    if (typeControl?.value === PromoType.Percentage && valueControl?.value > 100) {
      valueControl?.setErrors({ maxPercentage: true });
    }
  }

  loadPromocodes() {
    this.isLoading = true;
    this.promoService.getInitialPromocodes().subscribe({
      next: (data: any) => {
        const arr = Array.isArray(data) ? data :
          (data as any).result || (data as any).value || (data as any).data || [];
        this.promocodes = arr.map((p: any) => this.normalizePromoCode(p));
        this.isLoading = false;
      },
      error: () => {
        this.showError('Failed to load promocodes');
        this.isLoading = false;
      }
    });
  }

  isSearchLoading = false;

  onSearch() {
    this.isSearchLoading = true;

    setTimeout(() => {
      const code = this.filterForm.get('code')?.value;
      this.appliedFilters = {
        code: typeof code === 'object' && code !== null ? (code.code || code.name || '') : (code || ''),
        promoStatus: this.filterForm.get('promoStatus')?.value || ''
      };
      this.first = 0; 
      this.isSearchLoading = false;
    }, 1000);
  }

  filterNames(event: any) {
    const query = event.query;
    if (query && query.length >= 3) {
      this.isSearching = true;
      this.promoService.searchPromocodeByCode(query).subscribe({
        next: (response: any) => {
          const data = response?.result || response?.value || response?.data || response;
          if (Array.isArray(data)) {
            this.names = data.map((item: any) => {
              if (typeof item === 'string') return { name: item };
              return { name: item.Code || item.code || item.name || item };
            });
          } else {
            this.names = [];
          }
          this.isSearching = false;
        },
        error: (err) => {
          console.error('Error searching promocodes:', err);
          this.names = [];
          this.isSearching = false;
        }
      });
    } else {
      this.names = [];
    }
  }

  filteredPromoCodes() {
    const codeFilter = (this.appliedFilters.code || '').toLowerCase();
    const statusFilter = this.appliedFilters.promoStatus;

    return this.promocodes.filter(p => {
      const matchesCode = !codeFilter || p.code.toLowerCase().includes(codeFilter);
      const matchesStatus = !statusFilter || p.status === statusFilter;

      return matchesCode && matchesStatus;
    });
  }

  getUsagePercentage(used: number, limit: number): number {
    return limit > 0 ? (used / limit) * 100 : 0;
  }

  addPromoCode() {
    this.isEditMode = false;
    this.submitted = false;
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);

    this.promoForm.reset({
      id: 0,
      code: '',
      description: '',
      type: PromoType.Percentage,
      value: null,
      usageLimit: null,
      validityFrom: today.toISOString().split('T')[0],
      validityTo: nextMonth.toISOString().split('T')[0],
      status: 'Active'
    });
    this.displayDialog = true;
  }

  editPromoCode(promo: PromoCode) {
    this.isEditMode = true;
    this.submitted = false;
    this.promoForm.patchValue({
      id: promo.id,
      code: promo.code,
      description: promo.description,
      type: promo.type,
      value: promo.value,
      usageLimit: promo.usageLimit,
      validityFrom: promo.validityFrom?.split('T')[0],
      validityTo: promo.validityTo?.split('T')[0],
      status: promo.status
    });
    this.displayDialog = true;
  }

  savePromoCode() {
    this.submitted = true;

    if (!this.promoForm.valid) {
      this.showError('Please fill in all required fields correctly');
      this.markFormGroupTouched(this.promoForm);
      return;
    }

    const formValue = this.promoForm.value;
    const isDuplicate = this.promocodes.some(p =>
      p.code.toLowerCase() === formValue.code?.trim().toLowerCase() &&
      p.id !== (formValue.id || 0)
    );

    if (isDuplicate) {
      this.showError('Promo code with this name already exists');
      return;
    }

    const payload = this.buildPayload();
    const request = this.isEditMode
      ? this.promoService.updatePromocode(formValue.id, payload as UpdatePromocodeRequest)
      : this.promoService.createPromocode(payload as CreatePromocodeRequest);

    request.subscribe({
      next: (response: any) => this.handleSaveSuccess(response),
      error: (err: any) => this.handleSaveError(err)
    });
  }

  private buildPayload() {
    const formValue = this.promoForm.value;
    const fromDate = new Date(formValue.validityFrom);
    const toDate = new Date(formValue.validityTo);

    const payload: any = {
      code: formValue.code?.trim().toUpperCase(),
      description: formValue.description?.trim() || '',
      type: Number(formValue.type),
      value: Number(formValue.value),
      usageLimit: Number(formValue.usageLimit),
      validityFrom: fromDate.toISOString(),
      validityTo: toDate.toISOString()
    };

    if (this.isEditMode && formValue.id) {
      payload.id = formValue.id;
    }

    return payload;
  }

  private handleSaveSuccess(response: any) {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: `Promocode ${this.isEditMode ? 'updated' : 'created'} successfully`
    });

    const normalized = this.normalizePromoCode(response);

    if (this.isEditMode) {
      const idx = this.promocodes.findIndex(p => p.id === normalized.id);
      if (idx !== -1) this.promocodes[idx] = normalized;
    } else {
      this.promocodes = [normalized, ...this.promocodes];
    }

    this.displayDialog = false;
    this.submitted = false;
  }

  private handleSaveError(err: any) {
    let detail = `Failed to ${this.isEditMode ? 'update' : 'create'} promo code`;

    if (err.status === 500) {
      detail = 'Server Error: Possible duplicate code, invalid date, or database constraint';
      if (err.error?.message) detail += `\n${err.error.message}`;
    } else if (err.status === 400 && err.error?.errors) {
      detail = Object.entries(err.error.errors)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
        .join('\n');
    } else if (err.error?.message) {
      detail = err.error.message;
    }

    this.messageService.add({
      severity: 'error',
      summary: `API Error (${err.status})`,
      detail,
      life: 10000
    });
  }

  toggleStatus(promo: PromoCode) {
    this.promoService.updatePromocodeStatus(promo.id).subscribe({
      next: () => {
        promo.isActive = !promo.isActive;
        promo.status = promo.isActive ? 'Active' : 'Inactive';
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Status updated'
        });
      },
      error: () => this.showError('Failed to update status')
    });
  }

  copyCode(code: string) {
    navigator.clipboard.writeText(code).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Code copied to clipboard'
      });
    });
  }

  getTypeLabel(typeValue: number): string {
    return typeValue === PromoType.Percentage ? 'Percentage' : 'Fixed Amount';
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private showError(detail: string) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail });
  }

  private normalizePromoCode(p: any): PromoCode {
    const isActive = p.status === 0;

    return {
      id: p.Id ?? p.id ?? p.DiscountId ?? p.discountId ?? 0,
      code: p.Code ?? p.code ?? '',
      description: p.Description ?? p.description ?? '',
      type: p.Type ?? p.type ?? 0,
      value: p.Value ?? p.value ?? 0,
      usageLimit: p.UsageLimit ?? p.usageLimit ?? 0,
      usageCount: p.UsageCount ?? p.usageCount ?? 0,
      validityFrom: p.ValidityFrom ?? p.validityFrom ?? '',
      validityTo: p.ValidityTo ?? p.validityTo ?? '',
      isActive: isActive,
      status: p.status === 0 ? 'Active' : 'Inactive'
    };
  }

  get code() { return this.promoForm.get('code'); }
  get description() { return this.promoForm.get('description'); }
  get type() { return this.promoForm.get('type'); }
  get value() { return this.promoForm.get('value'); }
  get usageLimit() { return this.promoForm.get('usageLimit'); }
  get validityFrom() { return this.promoForm.get('validityFrom'); }
  get validityTo() { return this.promoForm.get('validityTo'); }
}